import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Print, ArrowBack, Download } from "@mui/icons-material";
import invoiceService from "../../services/invoiceService";

// Same PDF the "invoice issued" email attaches — ownership-checked
// server-side, so a customer only ever sees their own invoice here, and
// what they print/download always matches what was emailed.
const CustomerInvoicePrint = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [pdfUrl, setPdfUrl] = useState(null);
    const [filename, setFilename] = useState("Invoice.pdf");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const urlRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        invoiceService
            .getPdf(id)
            .then((response) => {
                if (cancelled) return;
                const disposition = response.headers?.["content-disposition"] || "";
                const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) setFilename(filenameMatch[1]);

                const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                urlRef.current = url;
                setPdfUrl(url);
            })
            .catch(() => !cancelled && setError(true))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
            if (urlRef.current) window.URL.revokeObjectURL(urlRef.current);
        };
    }, [id]);

    const handleDownload = () => {
        if (!pdfUrl) return;
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handlePrint = () => {
        const frame = document.getElementById("invoice-pdf-frame");
        if (frame) {
            frame.contentWindow?.focus();
            frame.contentWindow?.print();
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error || !pdfUrl) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="error">Failed to load invoice PDF.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            <Box className="no-print" sx={{ display: "flex", gap: 1, mb: 2, flexShrink: 0 }}>
                <Button startIcon={<ArrowBack />} variant="outlined" size="small" onClick={() => navigate(-1)}>
                    Back
                </Button>
                <Button startIcon={<Print />} variant="contained" size="small" onClick={handlePrint}>
                    Print / Save as PDF
                </Button>
                <Button startIcon={<Download />} variant="outlined" size="small" onClick={handleDownload}>
                    Download
                </Button>
            </Box>

            <Box sx={{ flex: 1 }}>
                <iframe
                    id="invoice-pdf-frame"
                    title="Invoice PDF"
                    src={pdfUrl}
                    style={{ width: "100%", height: "100%", border: "none" }}
                />
            </Box>

            <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
        }
      `}</style>
        </Box>
    );
};

export default CustomerInvoicePrint;
